// R-fix-2026-08-20 (v2): 项目可见性路由守卫, 用 <Navigate> 同步组件消除 race condition
//
// 场景: 用户 A 选项目 116, 关浏览器; 用户 B 登录, 浏览器 tab 还停在 /schedule/116。
// 此时 useParams().projectId = 116, Schedule mount, useApiQuery enabled:Number(pid) > 0 →
// 发请求 → 后端 verify_project_access 抛 403。
//
// v1 (commit 597cab75): useEffect + navigate 异步, mount 后下一帧才跳走, 期间 children
//      已 render 并发了 useApiQuery → 实际看到的是 4 个 design 子查询 403
//      (view/full, reviews, changes, deliverables) 在 navigate 生效前已飞到后端
// v2 (本版): render 阶段就判断, 无权时返回 <Navigate>, React 不会先 render children
//
// 跳转目标 (用户拍板 2026-08-20):
//   - 用户有可见项目: /schedule/<projects[0].id>
//     (按最近访问排序, B 任务在 ProjectsProvider 端用 localStorage 实现)
//   - 用户无可见项目: /schedule/0 (Schedule v6 dc7bd08c 已支持空骨架)
//   - 不再跳 /projects: 避免 v3 双 403 陷阱
//     (无 projects 权限的用户被送到 /projects 又触发 RequireModule 403, 体验更糟)
//
// 与 RequireModule 的职责边界:
//   - RequireModule: 检查 module 权限 (canViewModule)
//   - ProjectGuard: 检查 project 可见性 (用户是否在被分配/可见的项目列表中)
//   两者都通过才渲染页面。

import { useEffect } from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import { useProjects, useProjectsFetched, useAuth, setLastVisited } from '../../hooks/useAuth'

/**
 * 项目可见性路由守卫。
 * - children: 实际页面
 * - 在 RequireModule 之后包 children
 */
export default function ProjectGuard({ children, module }) {
  const { projectId } = useParams()
  const location = useLocation()
  // R-fix-2026-08-22 v2: 从 URL path 第一段取路由 key(URL 实际是 kebab-case,例如 /site-contact/123,
  // 而 module prop 是 snake_case 不能直接拼 — 之前用 module 拼导致 404)
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const urlKey = pathSegments[0] || 'projects'  // 第一个 segment 就是路由 key
  const projects = useProjects()
  const isFetched = useProjectsFetched()
  const { user } = useAuth()

  const pid = Number(projectId)

  // R-fix-2026-08-24 (R5-D4-006): render 期副作用 anti-pattern → 包进 useEffect
  // (React 18 StrictMode mount→unmount→mount 双调用,任何 props/user 变化也 re-render,
  //  都会写一次 lastVisited,污染 ts + 触发冗余 IO)
  useEffect(() => {
    if (pid > 0 && projects.some(p => p.id === pid) && module && user?.id) {
      setLastVisited(user.id, module, pid)
    }
    // 仅依赖 [pid, module, user?.id] — projects 入 deps 会过度执行
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid, module, user?.id])

  // pid <= 0: 各页面自己 enabled:false 处理空骨架, 这里不动
  if (pid <= 0) return children
  // projects 还在 fetch: 不校验, 避免误判空数组; 返回 null 不渲染 children,
  // 避免 children 在 projects 加载完之前误发请求
  if (!isFetched) return null
  // 用户可见项目含 pid: 通过 (lastVisited 已在 useEffect 写入)
  if (projects.some(p => p.id === pid)) {
    return children
  }

  // R-fix-2026-08-22: 无权访问 pid: 同步跳走, 不让 children render
  // 跳到同 module 下的第一个可见项目(保留用户原访问路径),而不是跨 module 跳 /schedule。
  // 无可见项目时跳同 module 的 /0 空骨架(各 page 已支持 pid=0),不跨 module 硬跳 /projects
  // (无 projects 权限的用户会被 /projects 的 RequireModule 403 卡死,且 /projects/0 无路由 → 404)。
  if (projects.length > 0) {
    return <Navigate to={`/${urlKey}/${projects[0].id}`} replace />
  }
  return <Navigate to={`/${urlKey}/0`} replace />
}
